// import "./App.css";
import React, { Suspense, lazy, useState, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import Home from "./pages/Home";
import Header from "./atoms/Header";
import { Footer } from "./atoms/Footer";
import { Layout } from "./HOC/Layout";
import Breadcrumbs from "./atoms/Breadcrumbs";
import { Col, Row } from "react-bootstrap";
import Buttons from "./atoms/Buttons";
import { IWantToCombo } from "./molecules/IWantToCombo";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "./redux/actions";
import LoadingSpinner from "./atoms/Loader";
import NotFound from "./atoms/NotFound";
import oidcConfig from "./redux/helpers/oidcConfig";
import { getUser, login, logout } from "./redux/actions/service/AuthService";
import RetirementApplication from "./pages/Plan/RetirementApplication";
import Investments from "./pages/Manage/Investments";
import { ModalOverlay } from "./atoms/Modal";
import Withdrawal from "./pages/Manage/Withdrawal";
import { UpscopeProvider } from "@upscopeio/react";
import PageNotFound from "./molecules/PageNotFound";
import { ErrorMessages } from "./constants/constants";
//prettier-ignore
const AddBeneficiaries = lazy(
  () => import("./pages/Account/Beneficiaries/add-beneficiaries")
);
const Beneficiaries = lazy(() => import("./pages/Account/Beneficiaries"));
const Account = lazy(() => import("./pages/Account"));
const Learn = lazy(() => import("./pages/Learn"));
const Manage = lazy(() => import("./pages/Manage"));
const Plan = lazy(() => import("./pages/Plan"));
const Savings = lazy(() => import("./pages/Savings"));
const GetinTouch = lazy(() => import("./pages/FooterPages/GetinTouch"));
const CookiePolicy = lazy(() => import("./pages/FooterPages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/FooterPages/PrivacyPolicy"));
const Accessibility = lazy(() => import("./pages/FooterPages/Accessibility"));
//prettier-ignore
const TermsnCondition = lazy(
  () => import("./pages/FooterPages/Termsnconditions")
);
const Video = lazy(() => import("./pages/Learn/Video"));
const Read = lazy(() => import("./pages/Learn/Read"));
const Webinars = lazy(() => import("./pages/Learn/Webinars"));
const Support = lazy(() => import("./pages/Learn/Support"));
const ArticleLanding = lazy(() => import("./pages/Learn/ArticleLanding"));
const About = lazy(() => import("./pages/Learn/About"));
const UsefulLinks = lazy(() => import("./pages/Learn/usefulLinks"));

//prettier-ignore
const RegularContribution = lazy(
  () => import("./pages/Manage/RegularContribution")
);
const AVContribution = lazy(() => import("./pages/Manage/AVContribution"));
const MessagesAndDocuments = lazy(
  () => import("./pages/Account/MessagesAndDocuments"),
);
const AccountSettings = lazy(() => import("./pages/Account/AccountSettings"));
const CommunicationPreferences = lazy(
  () => import("./pages/Account/CommunicationPreferences"),
);
const BoostYourSavings = lazy(() => import("./pages/Manage/BoostYourSavings"));
const ProductOverview = lazy(() => import("./pages/ProductOverview"));
const RetirementCalculator = lazy(
  () => import("./pages/Plan/RetirementCalculator"),
);
const Feedback = lazy(() => import("./pages/FooterPages/Feedback"));

const App = () => {
  const [badgeCount, setBadgeCount] = React.useState(null); // the lifted state
  const getBadgeCount = (index) => {
    setBadgeCount(index);
  };
  /* eslint-disable no-unused-vars */
  const {
    fetching: homeFetching,
    data: homeData,
    error: homeError,
  } = useSelector((state) => state.homePromo);
  const {
    fetching: investorFetch,
    data: investorData,
    error: investorError,
  } = useSelector((state) => state.investor);
  const {
    fetching: chatTokenFetch,
    data: chatTokenData,
    error: chatTokenError,
  } = useSelector((state) => state.chatToken);
  const { fetching, data, error } = useSelector((state) => state.memberType);
  const {
    fetching: refreshTokenFetch,
    data: refreshTokenData,
    error: refreshTokenError,
  } = useSelector((state) => state.refreshToken);
  const dispatch = useDispatch();
  const {
    getInvestorId,
    getMemberType,
    apiCallWithNewToken,
    getChatToken,
    homePromoContent,
  } = bindActionCreators(actionCreators, dispatch);
  const [user, setUser] = useState(null);
  //TODO - Smoke testing requirement
  async function isUrlAvailable(url) {
    try {
      const response = await fetch(url, {
        method: "GET",
      });

      // If we don't get a network error, we assume it's reachable
      // Note: 'no-cors' mode limits what we can check
      return response.ok;
    } catch (error) {
      console.error("URL check failed:", error);
      return false;
    }
  }

  useEffect(() => {
    const fetchUserData = async () => {
      // Try to restore user from localStorage first
      const storedUser = window.localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          const isValidUser =
            parsed &&
            typeof parsed === "object" &&
            typeof parsed.id_token === "string" &&
            parsed.id_token.length < 5000;

          if (!isValidUser) {
            // Remove only corrupted user payload; do not touch token keys.
            window.localStorage.removeItem("user");
            return;
          }

          // Keep required auth fields so refresh/access token flow is not broken.
          const safeUser = {
            id_token: parsed.id_token,
            scope: parsed.scope ?? "",
            profile: parsed.profile ?? {},
            access_token: parsed.access_token ?? "",
            refresh_token: parsed.refresh_token ?? "",
          };

          window.localStorage.setItem("user", JSON.stringify(safeUser));
          setUser(safeUser);

          // Restore tokens into session storage for API calls in this tab.
          if (parsed.access_token) {
            window.sessionStorage.setItem("token", parsed.access_token);
            window.localStorage.setItem("token", parsed.access_token);
          }
          if (parsed.refresh_token) {
            window.sessionStorage.setItem(
              "refresh_token",
              parsed.refresh_token,
            );
            window.localStorage.setItem("refresh_token", parsed.refresh_token);
          }

          // Remove legacy OIDC user payload from session storage (privacy hardening)
          window.sessionStorage.removeItem(
            `oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`,
          );
          return;
        } catch (e) {
          // Remove corrupted or malicious data
          window.localStorage.removeItem("user");
        }
      }

      // If not in storage, try to fetch user
      try {
        const userData = await getUser();
        if (userData) {
          const safeUser = {
            id_token: userData.id_token,
            scope: userData.scope ?? "",
            profile: userData.profile ?? {},
            access_token: userData.access_token ?? "",
            refresh_token: userData.refresh_token ?? "",
          };
          window.localStorage.setItem("user", JSON.stringify(safeUser));
          // Remove legacy OIDC user payload from session storage (privacy hardening)
          window.sessionStorage.removeItem(
            `oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`,
          );
          if (userData.access_token) {
            window.sessionStorage.setItem("token", userData.access_token);
            window.localStorage.setItem("token", userData.access_token);
          }
          if (userData.refresh_token) {
            window.sessionStorage.setItem(
              "refresh_token",
              userData.refresh_token,
            );
            window.localStorage.setItem(
              "refresh_token",
              userData.refresh_token,
            );
          }
          setUser(safeUser);
        } else {
          // Only call login if nothing is found
          //TODO - need to re-enable login and remove redirect once smoke testing is complete
          const url =
            process.env.REACT_APP_TPT_SSO_AUTHORITY +
            "/.well-known/openid-configuration";
          isUrlAvailable(url).then((available) => {
            if (available) {
              login();
            } else {
              window.location.href = process.env.REACT_APP_TPT_SSO_AUTHORITY; // Redirect
            }
          });
        }
      } catch (error) {
        // Handle error (show error UI, etc.)
        setUser(null);
        return <NotFound err={error ? error : ErrorMessages.INVALID_USER} />;
      }
    };
    fetchUserData();
  }, []);
  useEffect(() => {
    if (investorError === "CLIENT_ERROR") {
      setShowRefreshTokenModal(true);
      apiCallWithNewToken(dispatch);
    } else if (user) {
      getInvestorId();
    }
  }, [user]);
  useEffect(() => {
    if (investorData.investorId) {
      // MOCKAPI_INVESTOR_ID?.includes(investorData.investorId)
      //   ? getMemberTypeMock(investorData.investorId)
      //   : getMemberType(investorData.investorId);
      homePromoContent(investorData.investorId);
      getMemberType(investorData.investorId);
      getChatToken(investorData.investorId);
    }
  }, [investorData]);

  const [showRefreshTokenModal, setShowRefreshTokenModal] = useState(false);
  const [showSignoutModal, setShowSignoutModal] = useState(false);
  useEffect(() => {
    if (refreshTokenFetch) {
      setShowRefreshTokenModal(true);
    } else {
      setShowRefreshTokenModal(false);
    }
  }, [refreshTokenFetch]);
  // initiate zendesk chat window based on chat token
  useEffect(() => {
    const zendeskKey = process.env.REACT_APP_ZENDESK_SCRIPT_KEY;

    if (zendeskKey) {
      const script = document.createElement("script");
      script.id = "ze-snippet";
      script.src = zendeskKey;
      script.async = true;
      script.onload = () => {
        if (chatTokenData.token) {
          window.zE("messenger", "loginUser", function (callback) {
            callback(chatTokenData.token);
          });
          window.zE("messenger", "show");
        } else {
          window.zE("messenger", "hide");
        }
      };
      document.body.appendChild(script);
    } else {
      console.error(
        "Zendesk script is not defined in the environment variables.",
      );
    }
  }, [chatTokenData]);

  const logoutUser = () => {
    setShowSignoutModal(false);
    window.localStorage.clear();
    window.sessionStorage.clear();
    logout();
  };
  if (investorFetch || fetching || homeFetching) {
    return <LoadingSpinner appLanding={true} width="100" />;
  }

  if (!user) {
    return <LoadingSpinner appLanding={true} width="100" />;
  }

  return (
    <>
      {error || investorError || homeError || !(data && data.memberType) ? (
        <>
          <NotFound
            err={
              error || investorError || homeError
                ? error
                  ? error
                  : investorError
                    ? investorError
                    : homeError
                : ErrorMessages.INVALID_USER
            }
          />
        </>
      ) : (
        <>
          <Layout>
            <UpscopeProvider
              apiKey={process.env.REACT_APP_UP_SCOPE_KEY}
              enabled={true}
              uniqueId={data?.investorId}
            >
              <div className="main-container">
                <div className="navigation-header">
                  <Header
                    badgeCount={badgeCount}
                    memberObj={data}
                    logoutUser={() => setShowSignoutModal(true)}
                  />
                </div>
                <div className="pag-content">
                  <Row>
                    <Col lg={6} md={6} sm={6} xs={12} className="mb-2">
                      {" "}
                      <Breadcrumbs />
                    </Col>
                    <Col
                      lg={6}
                      md={6}
                      sm={6}
                      xs={12}
                      className="d-none d-sm-block"
                    >
                      <Buttons
                        className="outline-success d-none d-sm-block text-end"
                        name="Sign out"
                        handleClick={() => setShowSignoutModal(true)}
                      />
                      <IWantToCombo memberObj={data} />
                    </Col>
                  </Row>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Home
                            getBadgeCount={getBadgeCount}
                            memberObj={data}
                            homeData={homeData}
                          />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/account-details"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Account memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/messages-and-documents"
                      element={
                        <Suspense fallback={<>...</>}>
                          <MessagesAndDocuments
                            memberObj={data}
                            getBadgeCount={getBadgeCount}
                          />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/account-settings"
                      element={
                        <Suspense fallback={<>...</>}>
                          <AccountSettings />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/communication-preferences"
                      element={
                        <Suspense fallback={<>...</>}>
                          <CommunicationPreferences memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/beneficiaries"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Beneficiaries memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/beneficiaries/add-beneficiaries"
                      element={
                        <Suspense fallback={<>...</>}>
                          <AddBeneficiaries />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/home"
                      exact
                      element={
                        <Suspense fallback={<>...</>}>
                          <Home />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Learn memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/manage"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Manage memberObj={data} />
                        </Suspense>
                      }
                    />
                    {/* Manage child  routes starts */}
                    <Route
                      path={`/manage/regular-contribution`}
                      element={
                        <Suspense fallback={<>...</>}>
                          <RegularContribution memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path={`/manage/av-contribution`}
                      element={
                        <Suspense fallback={<>...</>}>
                          <AVContribution memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/manage/investments"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Investments memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/manage/withdrawal"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Withdrawal memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/manage/boost-your-savings"
                      element={
                        <Suspense fallback={<>...</>}>
                          <BoostYourSavings memberObj={data} />
                        </Suspense>
                      }
                    />
                    {/* Manage child  routes ends */}
                    <Route
                      path="/feedback"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Feedback memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/plan"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Plan memberObj={data} tab={"plan"} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/plan/retirement-application"
                      element={
                        <Suspense fallback={<>...</>}>
                          <RetirementApplication memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/Savings"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Savings memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn/view-all"
                      element={
                        <Suspense fallback={<>...</>}>
                          {<ArticleLanding memberObj={data} />}
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn/webinar/:webinar"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Webinars memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn/article/:article"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Read memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/help-guides/:guides"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Read memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn/support/:support"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Support memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn/video/:video"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Video memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn/:variable"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Learn memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/help-guides/video"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Video memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/useful-links/"
                      element={
                        <Suspense fallback={<>...</>}>
                          <UsefulLinks memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/learn/about-tpt/:about"
                      element={
                        <Suspense fallback={<>...</>}>
                          <About memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/Product-overview/*"
                      element={
                        <Suspense fallback={<>...</>}>
                          <ProductOverview memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/plan/future-calculator/"
                      element={
                        <Suspense fallback={<>...</>}>
                          <RetirementCalculator memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/get-in-touch/*"
                      element={
                        <Suspense fallback={<>...</>}>
                          <GetinTouch memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/cookie-policy/*"
                      element={
                        <Suspense fallback={<>...</>}>
                          <CookiePolicy memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/privacy-policy/*"
                      element={
                        <Suspense fallback={<>...</>}>
                          <PrivacyPolicy memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/accessibility/*"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Accessibility memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/terms-conditions/"
                      element={
                        <Suspense fallback={<>...</>}>
                          <TermsnCondition memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/help-guides/webinar"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Webinars memberObj={data} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guidance/*"
                      element={
                        <Suspense fallback={<>...</>}>
                          <Plan memberObj={data} tab={"guidance"} />
                        </Suspense>
                      }
                    />
                    {/* Catch-all route for 404 Page Not Found */}
                    <Route path="*" element={<PageNotFound />} />
                  </Routes>
                </div>
              </div>
              <Footer footerContent={homeData} />
            </UpscopeProvider>
          </Layout>

          <ModalOverlay
            popup="refreshToken"
            show={showRefreshTokenModal}
            onHide={(e) => {
              if (e !== undefined) {
                e.preventDefault();
              }
              setShowRefreshTokenModal(false);
            }}
            modaltitle="Session Expired"
          />
          <ModalOverlay
            popup="signoutModal"
            show={showSignoutModal}
            onHide={(e) => {
              if (e !== undefined) {
                e.preventDefault();
              }
              setShowSignoutModal(false);
            }}
            signoutConfirmHandler={logoutUser}
          />
        </>
      )}
    </>
  );
};
export default App;
